import { InputRemoteFileSelector as RemoteFileSelector } from './remote-file-selection/RemoteFileSelector';
import {
  executeDelimitedParser,
  fileExtensionRegex,
  InputFileDetails,
} from './client-file-selection';
import { ClientFileSelector } from './client-file-selection/ClientFileSelector';
import { InputFileSelectionOptions } from './models';
import { OneClickInputRemoteFileSelector } from './remote-file-selection/OneClickRemoteFileSelection';
import { modalUtils } from '@/app/core/utils';
import {
  FieldMappings,
  InputFileUploadResult,
} from '@/app/features/products/common/components/frontend-processing/models';
import {
  acceptedExtensions,
  HeaderInfo,
  InputLabelsMap,
} from '@ssar-app/domain';
import { useRef, useCallback } from 'react';
import { Modal } from '@ssar-app/uikit';
import { HStack } from '@chakra-ui/react';

export interface InputFileSelectorProps {
  onFileClicked: () => void;
  onFileSelected: (details: InputFileDetails) => void;
  onCancel: () => void;
  isCompact?: boolean;
  setIsRemoteFile: (arg0: boolean) => void;
  subCode: string;
  onSuccess: (uploadResult: InputFileUploadResult) => void;
  inputLabelsMap?: InputLabelsMap;
  inputFileSelectionOptions: InputFileSelectionOptions;
  productName: string;
  oneClickRemoteFileAccess?: boolean;
  isModalVisible: boolean;
  setIsModalVisible: (arg0: boolean) => void;
}
export const InputFileSelector = (props: InputFileSelectorProps) => {
  const { isCompact, inputFileSelectionOptions } = props;
  const firstRow = useRef<string[]>([]);

  const beforeUpload = (
    headersInfo: HeaderInfo,
    file?: File,
    content?: string[],
    experianFileName?: string,
    clientFileName?: string,
    totalRecords?: number,
    intentType?: string,
    fileId?: string,
  ): void => {
    props.setIsRemoteFile(file === undefined);
    props.onFileClicked();
    const extension = fileExtensionRegex
      .exec(clientFileName || file?.name || '')?.[1]
      ?.toLowerCase();
    if (!extension || !acceptedExtensions.includes(extension)) {
      props.onCancel();
      modalUtils.showError(
        `Only files with extensions ${acceptedExtensions} are allowed. You have selected a file with an extensions ${extension}`,
      );
    } else {
      if (headersInfo.hasHeaders == null) {
        Modal.confirm({
          header: 'Unable to process headers',
          bodyProps: { pt: 4 },
          content: 'Does the file has headers?',
          cancelButtonProps: {
            colorScheme: 'gray',
            variant: 'solid',
            size: 'sm',
            fontSize: 'sm',
            boxShadow: 'lg',
            bg: 'white',
            border: '1px',
            borderColor: 'gray.200',
            _hover: {
              background: 'white',
              color: 'blue.800',
              borderColor: 'blue.800',
            },
          },
          okButtonProps: {
            size: 'sm',
          },
          onOk() {
            headersInfo = {
              hasHeaders: true,
              actualHeaders: content?.[0].split(',') || firstRow.current,
              detectedHeaders:
                content?.[0].split(',').map((_ele) => ['']) ||
                firstRow.current.map((_ele) => ['']),
            };
            callDelimitedParser(
              headersInfo,
              file,
              content,
              experianFileName,
              clientFileName,
              totalRecords,
              intentType,
              fileId,
            );
          },
          onCancel() {
            headersInfo = {
              hasHeaders: false,
              actualHeaders:
                content?.[0].split(',').map((_ele) => '') ||
                firstRow.current.map((_ele) => ''),
              detectedHeaders:
                content?.[0].split(',').map((_ele) => ['']) ||
                firstRow.current.map((_ele) => ['']),
            };
            callDelimitedParser(
              headersInfo,
              file,
              content,
              experianFileName,
              clientFileName,
              totalRecords,
              intentType,
              fileId,
            );
          },
          okText: 'Yes',
          cancelText: 'No',
        });
      } else {
        callDelimitedParser(
          headersInfo,
          file,
          content,
          experianFileName,
          clientFileName,
          totalRecords,
          intentType,
          fileId,
        );
      }
    }
  };

  const callDelimitedParser = (
    headersInfo: HeaderInfo,
    file?: File,
    content?: string[],
    experianFileName?: string,
    clientFileName?: string,
    totalRecords?: number,
    intentType?: string,
    fileId?: string,
  ): void => {
    executeDelimitedParser(
      headersInfo,
      file,
      content,
      experianFileName,
      clientFileName,
      totalRecords,
      intentType,
      fileId,
    )
      .then(async ({ inputfileDetails, error }) => {
        if (!inputfileDetails) {
          throw new Error(error);
        } else {
          props.onFileSelected(inputfileDetails);
        }
      })
      .catch((error) => {
        modalUtils.showError(error, null, props.onCancel, props.onCancel);
      });
  };

  const onFileUploadSuccess = useCallback(
    (
      clientFileName: string,
      experianFileName: string,
      fieldMappings: FieldMappings[],
      hasHeaders?: boolean,
      recordsExpected?: number,
    ): void => {
      props.onSuccess({
        clientFileName,
        experianFileName,
        fieldMappings,
        recordsExpected,
        hasHeaders,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onSuccess],
  );

  return (
    <>
      {isCompact && (
        <HStack>
          {inputFileSelectionOptions.canSelectClientFile && (
            <ClientFileSelector
              isCompact={isCompact}
              beforeUpload={beforeUpload}
              subCode={props.subCode}
              onFileClicked={props.onFileClicked}
              onFileSelected={props.onFileSelected}
              onCancel={props.onCancel}
              firstRow={firstRow}
            ></ClientFileSelector>
          )}
          {inputFileSelectionOptions.canSelectRemoteFile &&
            !props.oneClickRemoteFileAccess && (
              <RemoteFileSelector
                isCompact={isCompact}
                beforeUpload={beforeUpload}
                subCode={props.subCode}
                productName={props.productName}
                onSuccess={onFileUploadSuccess}
                inputLabelsMap={props.inputLabelsMap}
                isModalVisible={props.isModalVisible}
                setIsModalVisible={props.setIsModalVisible}
              ></RemoteFileSelector>
            )}
          {inputFileSelectionOptions.canSelectRemoteFile &&
            props.oneClickRemoteFileAccess && (
              <OneClickInputRemoteFileSelector
                isCompact={isCompact}
                beforeUpload={beforeUpload}
                subCode={props.subCode}
                productName={props.productName}
                onSuccess={onFileUploadSuccess}
                inputLabelsMap={props.inputLabelsMap}
                isModalVisible={props.isModalVisible}
                setIsModalVisible={props.setIsModalVisible}
              ></OneClickInputRemoteFileSelector>
            )}
        </HStack>
      )}
      {!isCompact && (
        <div>
          <HStack gap={7} mt={-1}>
            {inputFileSelectionOptions.canSelectClientFile && (
              <ClientFileSelector
                isCompact={isCompact}
                beforeUpload={beforeUpload}
                subCode={props.subCode}
                onFileClicked={props.onFileClicked}
                onFileSelected={props.onFileSelected}
                onCancel={props.onCancel}
                firstRow={firstRow}
              ></ClientFileSelector>
            )}
            {inputFileSelectionOptions.canSelectRemoteFile &&
              !props.oneClickRemoteFileAccess && (
                <RemoteFileSelector
                  isCompact={isCompact}
                  beforeUpload={beforeUpload}
                  subCode={props.subCode}
                  productName={props.productName}
                  onSuccess={onFileUploadSuccess}
                  inputLabelsMap={props.inputLabelsMap}
                  isModalVisible={props.isModalVisible}
                  setIsModalVisible={props.setIsModalVisible}
                ></RemoteFileSelector>
              )}
            {inputFileSelectionOptions.canSelectRemoteFile &&
              props.oneClickRemoteFileAccess && (
                <OneClickInputRemoteFileSelector
                  isCompact={isCompact}
                  beforeUpload={beforeUpload}
                  subCode={props.subCode}
                  productName={props.productName}
                  onSuccess={onFileUploadSuccess}
                  inputLabelsMap={props.inputLabelsMap}
                  isModalVisible={props.isModalVisible}
                  setIsModalVisible={props.setIsModalVisible}
                ></OneClickInputRemoteFileSelector>
              )}
          </HStack>
        </div>
      )}
    </>
  );
};
